import {
  EditableChip,
  Flex,
  FlexCenter,
  FlexColumn,
} from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as FailedRunIcon } from '@elementary/common-ui/assets/16x16/failed-run.svg';
import { ReactComponent as ModelErrorIcon } from '@elementary/common-ui/assets/16x16/model-error.svg';
import { ReactComponent as SyncFailureIcon } from '@elementary/common-ui/assets/16x16/sync-failure.svg';
import { ReactComponent as ClockCountdownIcon } from '@elementary/common-ui/assets/16x16/clock-countdown.svg';
import { formatTimeDuration } from '@elementary/common-ui/utils/datetime';
import { NO_ASSIGNEE_VALUE } from '../../../constants';
import useIncidentMetadataOptions from '../../../hooks/useIncidentMetadataOptions';
import IncidentTicketChip from '../../incidentTickets/IncidentTicketChip';
import { IncidentOverviewData } from '../../../types';

const ChipWithIcon = styled(FlexCenter)`
  gap: 4px;
  padding: 4px 6px;
`;

const InfoChip = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.black};
  white-space: nowrap;
`;

const Description = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray2};
  max-height: 180px;
  overflow-y: auto;
`;

type Props = {
  incident: IncidentOverviewData;
};

const IncidentMetadata = ({
  incident: {
    assignee,
    status,
    severity,
    startTime,
    runFailureEventCount,
    id,
    sourceType,
    testDescription,
    modelDescription,
  },
  incident,
}: Props) => {
  const {
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    updateIncident,
    isUpdating,
  } = useIncidentMetadataOptions();

  const isResolved = status === 'Resolved';
  const isEditDisabled = !canEdit || isResolved;

  const handleAssigneeChange = (value: string) => {
    updateIncident({
      incidentId: id,
      assignee: value,
    });
  };

  const handleStatusChange = (value: string) => {
    updateIncident({
      incidentId: id,
      status: value,
    });
  };

  const handleSeverityChange = (value: string) => {
    updateIncident({
      incidentId: id,
      severity: value,
    });
  };

  const description = testDescription || modelDescription;

  return (
    <FlexColumn gap={3}>
      {description && <Description>{description}</Description>}
      <Flex gap={1} alignItems="center">
        <EditableChip
          value={assignee || NO_ASSIGNEE_VALUE}
          options={assigneeOptions ?? []}
          disabled={isEditDisabled}
          onSave={handleAssigneeChange}
          showSearch
          isLoading={isUpdating}
        />
        <EditableChip
          value={capitalize(status)}
          options={statusOptions ?? []}
          disabled={isEditDisabled}
          onSave={handleStatusChange}
          isLoading={isUpdating}
        />
        <EditableChip
          value={capitalize(severity)}
          options={severityOptions ?? []}
          disabled={isEditDisabled}
          onSave={handleSeverityChange}
          isLoading={isUpdating}
        />

        <IncidentTicketChip incident={incident} />

        <ChipWithIcon>
          <ClockCountdownIcon />
          <InfoChip>Open {formatTimeDuration(startTime)}</InfoChip>
        </ChipWithIcon>
        <ChipWithIcon>
          {sourceType === 'test' ? (
            <FailedRunIcon />
          ) : sourceType === 'pipeline' ? (
            <SyncFailureIcon />
          ) : (
            <ModelErrorIcon />
          )}
          <InfoChip>{runFailureEventCount} Fails</InfoChip>
        </ChipWithIcon>
      </Flex>
    </FlexColumn>
  );
};

export default IncidentMetadata;
